/**
 * Game Animation System
 *
 * Provides animation infrastructure for jump sequences and other visual effects.
 * Includes timing calculations, easing functions, and input management.
 */

import * as THREE from 'three';
import { ANIMATION_CONFIG } from '../constants.js';
import { createStarTexture } from './stars.js';

/**
 * Animation timing calculator
 *
 * Calculates appropriate animation durations based on distance and configuration.
 * Ensures animations feel appropriate for the game's pacing.
 */
export class AnimationTimingCalculator {
  /**
   * Calculate travel duration based on distance
   *
   * Uses linear interpolation between min and max durations based on distance.
   * Short jumps use minimum duration to ensure visibility.
   * Long jumps use maximum duration to prevent tedium.
   *
   * @param {number} distance - Distance in light years
   * @returns {number} Duration in seconds
   */
  static calculateTravelDuration(distance) {
    const {
      MIN_TRAVEL_DURATION,
      MAX_TRAVEL_DURATION,
      MIN_DISTANCE,
      MAX_DISTANCE,
    } = ANIMATION_CONFIG;

    // Clamp to prevent negative durations and ensure animations don't exceed tedium threshold
    const clampedDistance = Math.max(
      MIN_DISTANCE,
      Math.min(distance, MAX_DISTANCE)
    );

    // Linear scaling creates natural feel where longer jumps take proportionally longer
    const t = (clampedDistance - MIN_DISTANCE) / (MAX_DISTANCE - MIN_DISTANCE);

    return (
      MIN_TRAVEL_DURATION + (MAX_TRAVEL_DURATION - MIN_TRAVEL_DURATION) * t
    );
  }

  /**
   * Calculate zoom transition duration
   *
   * Fixed duration for all camera transitions to maintain consistent feel.
   *
   * @returns {number} Duration in seconds
   */
  static calculateZoomDuration() {
    return ANIMATION_CONFIG.ZOOM_DURATION;
  }

  /**
   * Calculate total animation duration
   *
   * Includes zoom-in, travel, and zoom-out phases.
   * Total duration is calibrated to be between 3-5 seconds for good pacing.
   *
   * @param {number} distance - Distance in light years
   * @returns {number} Total duration in seconds
   */
  static calculateTotalDuration(distance) {
    const zoomIn = this.calculateZoomDuration();
    const travel = this.calculateTravelDuration(distance);
    const zoomOut = this.calculateZoomDuration();
    return zoomIn + travel + zoomOut;
  }
}

/**
 * Easing functions for smooth animations
 *
 * Provides mathematical functions for interpolating values with different
 * acceleration/deceleration curves.
 */
class EasingFunctions {
  /**
   * Ease-in-out cubic easing
   *
   * Provides smooth acceleration at the start and deceleration at the end.
   * Creates a polished, cinematic feel for camera transitions.
   *
   * Mathematical formula:
   * - First half (t < 0.5): accelerate using 4t³
   * - Second half (t ≥ 0.5): decelerate using 1 - (-2t + 2)³ / 2
   *
   * @param {number} t - Progress value between 0 and 1
   * @returns {number} Eased value between 0 and 1
   */
  static easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}

/**
 * Input lock manager
 *
 * Manages disabling and re-enabling player input during animations.
 * Prevents accidental actions while animations are playing.
 */
class InputLockManager {
  /**
   * Create input lock manager
   *
   * @param {Object} controls - Three.js OrbitControls instance
   */
  constructor(controls) {
    this.controls = controls;
    this.isLocked = false;
    this.originalControlsEnabled = null;
  }

  /**
   * Disable all player input controls
   *
   * Stores original control state for restoration and disables
   * camera controls to prevent interaction during animations.
   */
  lock() {
    if (this.isLocked) return;

    this.isLocked = true;
    this.originalControlsEnabled = this.controls.enabled;
    this.controls.enabled = false;
  }

  /**
   * Re-enable all player input controls
   *
   * Restores controls to their original state before locking.
   */
  unlock() {
    if (!this.isLocked) return;

    this.isLocked = false;
    if (this.originalControlsEnabled !== null) {
      this.controls.enabled = this.originalControlsEnabled;
    }
  }

  /**
   * Check if input is currently locked
   *
   * @returns {boolean} True if input is locked
   */
  isInputLocked() {
    return this.isLocked;
  }
}

/**
 * Create ship indicator sprite
 *
 * Creates a glowing red sprite to represent the player's ship during jump animations.
 * Uses the same texture creation pattern as star sprites for visual consistency.
 * Configured with red color, additive blending, and appropriate size.
 *
 * @returns {THREE.Sprite} Ship indicator sprite
 */
function createShipIndicatorSprite() {
  // Create glow texture for ship indicator
  const texture = createStarTexture();

  // Create sprite material with red color and additive blending
  const material = new THREE.SpriteMaterial({
    map: texture,
    color: ANIMATION_CONFIG.SHIP_INDICATOR_COLOR,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  // Create sprite
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(
    ANIMATION_CONFIG.SHIP_INDICATOR_SIZE,
    ANIMATION_CONFIG.SHIP_INDICATOR_SIZE,
    1
  );

  // Initially hidden until animation begins
  sprite.visible = false;

  return sprite;
}

/**
 * Create reticle for ship indicator
 *
 * Creates a circular reticle around the ship indicator to improve visibility.
 * Uses a LineLoop with manually created circle vertices for a simple ring.
 * Configured with cyan color for contrast against the red ship indicator.
 *
 * @returns {THREE.LineLoop} Reticle line object
 */
function createShipReticle() {
  // Create circle vertices manually
  const vertices = [];
  const segments = ANIMATION_CONFIG.RETICLE_SEGMENTS;
  const radius = ANIMATION_CONFIG.RETICLE_SIZE;

  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    vertices.push(
      new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0)
    );
  }

  // Create line geometry from vertices
  const geometry = new THREE.BufferGeometry().setFromPoints(vertices);

  // Create line material with cyan color
  const material = new THREE.LineBasicMaterial({
    color: ANIMATION_CONFIG.RETICLE_COLOR,
    transparent: true,
    opacity: 0.8,
    linewidth: ANIMATION_CONFIG.RETICLE_LINE_WIDTH,
  });

  // Create line loop (automatically closes the circle)
  const reticle = new THREE.LineLoop(geometry, material);

  // Initially hidden until animation begins
  reticle.visible = false;

  return reticle;
}

/**
 * Jump animation system
 *
 * Orchestrates the complete jump animation sequence including camera transitions,
 * ship indicator movement, and input locking.
 */
export class JumpAnimationSystem {
  /**
   * Create jump animation system
   *
   * @param {THREE.Scene} scene - Three.js scene
   * @param {THREE.Camera} camera - Three.js camera
   * @param {Object} controls - Three.js OrbitControls instance
   * @param {Array} starData - Array of star system data
   */
  constructor(scene, camera, controls, starData) {
    this.scene = scene;
    this.camera = camera;
    this.controls = controls;
    this.starData = starData;
    this.isAnimating = false;

    // Create ship indicator sprite (reused across all jumps)
    this.shipIndicator = createShipIndicatorSprite();
    this.scene.add(this.shipIndicator);

    // Create reticle for ship indicator
    this.shipReticle = createShipReticle();
    this.scene.add(this.shipReticle);

    // Create input lock manager
    this.inputLockManager = new InputLockManager(controls);

    // Store original camera state for restoration
    this.originalCameraState = null;

    // Reusable Vector3 objects for calculations to avoid allocations in hot paths
    this._tempVec1 = new THREE.Vector3();
    this._tempVec2 = new THREE.Vector3();
    this._tempVec3 = new THREE.Vector3();
    this._tempVec4 = new THREE.Vector3();

    // Reusable objects for side view calculation results
    this._sideViewPosition = new THREE.Vector3();
    this._sideViewLookAt = new THREE.Vector3();

    // Pre-allocated vectors for camera state storage
    this._storedCameraPosition = new THREE.Vector3();
    this._storedCameraTarget = new THREE.Vector3();
  }

  /**
   * Calculate side view camera position perpendicular to jump path
   *
   * Positions the camera to provide a cinematic side view of both stars.
   * The camera is placed perpendicular to the line between origin and destination,
   * at a distance that frames both stars comfortably in view.
   *
   * Uses cross product with up vector to find perpendicular direction.
   * Applies minimum distance constraint to prevent camera clipping for very close stars.
   *
   * PERFORMANCE NOTE: This method reuses pre-allocated Vector3 objects to avoid
   * allocations during jump animations. The returned objects are instance properties
   * that persist across jumps, eliminating garbage collection pressure.
   *
   * @param {THREE.Vector3} originPos - Origin star position
   * @param {THREE.Vector3} destPos - Destination star position
   * @param {number} distance - Distance between stars
   * @returns {Object} { position: THREE.Vector3, lookAt: THREE.Vector3 }
   */
  calculateSideViewPosition(originPos, destPos, distance) {
    // Position camera to look at midpoint for balanced framing of both stars
    this._tempVec1.addVectors(originPos, destPos).multiplyScalar(0.5);

    // Need normalized direction to calculate perpendicular vector via cross product
    this._tempVec2.subVectors(destPos, originPos).normalize();

    // Cross product with up vector gives perpendicular direction for side view
    this._tempVec3.set(0, 1, 0).crossVectors(this._tempVec2, this._tempVec3);

    // Parallel vectors produce zero cross product - use forward vector as fallback
    if (this._tempVec3.lengthSq() < ANIMATION_CONFIG.VECTOR_EPSILON) {
      this._tempVec3.set(0, 0, 1).crossVectors(this._tempVec2, this._tempVec3);
    }

    this._tempVec3.normalize();

    // Calculate camera distance from midpoint
    // Use multiplier to ensure both stars are comfortably in view
    // Apply minimum distance constraint for very close stars
    const baseDistance =
      distance * ANIMATION_CONFIG.SIDE_VIEW_DISTANCE_MULTIPLIER;
    const cameraDistance = Math.max(
      baseDistance,
      ANIMATION_CONFIG.MIN_SIDE_VIEW_DISTANCE
    );

    // Position camera perpendicular to jump path at calculated distance
    this._tempVec4
      .copy(this._tempVec1)
      .addScaledVector(this._tempVec3, cameraDistance);

    // Store results in reusable objects to avoid allocations
    this._sideViewPosition.copy(this._tempVec4);
    this._sideViewLookAt.copy(this._tempVec1);

    return {
      position: this._sideViewPosition,
      lookAt: this._sideViewLookAt,
    };
  }

  /**
   * Store current camera state for later restoration
   *
   * Should be called once at the start of a jump animation sequence.
   * Captures the camera position and look-at target before any transitions.
   */
  _storeCameraState() {
    this._storedCameraPosition.copy(this.camera.position);
    this._storedCameraTarget.copy(this.controls.target);
    this.originalCameraState = {
      position: this._storedCameraPosition,
      target: this._storedCameraTarget,
    };
  }

  /**
   * Restore camera to original state
   *
   * Should be called once at the end of a jump animation sequence.
   * Returns the camera to its position before the animation started.
   */
  _restoreCameraState() {
    if (this.originalCameraState) {
      this.camera.position.copy(this.originalCameraState.position);
      this.controls.target.copy(this.originalCameraState.target);
      this.controls.update();
      this.originalCameraState = null;
    }
  }

  /**
   * Animate camera transition with easing
   *
   * Smoothly transitions the camera from its current position to a target position
   * and look-at point using easeInOutCubic easing for a polished, cinematic feel.
   *
   * Uses requestAnimationFrame for smooth 60fps animation without blocking the main thread.
   *
   * PERFORMANCE CRITICAL: This method runs inside requestAnimationFrame (60fps).
   * All Vector3 objects are pre-allocated and reused via this._tempVec1-4 to avoid
   * garbage collection pressure. NEVER allocate objects inside this animation loop.
   *
   * @param {THREE.Vector3} targetPosition - Target camera position
   * @param {THREE.Vector3} targetLookAt - Target camera look-at point
   * @param {number} duration - Animation duration in seconds
   * @returns {Promise<void>} Resolves when transition completes
   */
  animateCameraTransition(targetPosition, targetLookAt, duration) {
    return new Promise((resolve) => {
      // Store start state for interpolation
      // Reuse temp vectors to avoid allocations
      const startPosition = this._tempVec1.copy(this.camera.position);
      const startLookAt = this._tempVec2.copy(this.controls.target);

      // Pre-allocate vectors for interpolation (reused each frame)
      const currentPosition = this._tempVec3;
      const currentLookAt = this._tempVec4;

      const startTime = performance.now();

      const animateCameraFrame = () => {
        const elapsed = (performance.now() - startTime) / 1000; // Convert to seconds
        const progress = Math.min(elapsed / duration, 1.0);

        // Apply easing function for smooth acceleration/deceleration
        const easedProgress = EasingFunctions.easeInOutCubic(progress);

        // Interpolate camera position using reusable vectors
        currentPosition.copy(startPosition).lerp(targetPosition, easedProgress);
        this.camera.position.copy(currentPosition);

        // Interpolate camera look-at target using reusable vectors
        currentLookAt.copy(startLookAt).lerp(targetLookAt, easedProgress);
        this.controls.target.copy(currentLookAt);

        // Update controls to apply new target
        this.controls.update();

        // Continue animation or resolve
        if (progress < 1.0) {
          requestAnimationFrame(animateCameraFrame);
        } else {
          resolve();
        }
      };

      // Start animation
      requestAnimationFrame(animateCameraFrame);
    });
  }

  /**
   * Animate ship indicator traveling between stars
   *
   * Displays the ship indicator at the origin star and animates it traveling
   * to the destination star using linear interpolation (constant velocity).
   * Uses requestAnimationFrame for smooth 60fps animation.
   *
   * Travel duration is calculated based on distance with min/max bounds to ensure
   * short jumps are visible and long jumps don't feel tedious.
   *
   * PERFORMANCE CRITICAL: This method runs inside requestAnimationFrame (60fps).
   * All Vector3 objects are pre-allocated and reused via this._tempVec1 to avoid
   * garbage collection pressure. NEVER allocate objects inside this animation loop.
   *
   * @param {THREE.Vector3} originPos - Origin star position
   * @param {THREE.Vector3} destPos - Destination star position
   * @param {number} distance - Distance between stars in light years
   * @param {Function|null} onNearEnd - Optional callback invoked once when travel
   *   progress reaches the configured encounter reveal threshold
   * @returns {Promise<void>} Resolves when travel animation completes
   */
  animateShipTravel(originPos, destPos, distance, onNearEnd = null) {
    return new Promise((resolve) => {
      // Calculate travel duration based on distance with bounds
      const duration =
        AnimationTimingCalculator.calculateTravelDuration(distance);

      // Position ship indicator at origin star
      this.shipIndicator.position.copy(originPos);

      // Position reticle at origin star
      this.shipReticle.position.copy(originPos);

      // Make ship indicator and reticle visible
      this.shipIndicator.visible = true;
      this.shipReticle.visible = true;

      // Reuse temp vector for interpolated position (avoid allocations in loop)
      const currentPosition = this._tempVec1;

      // Track whether onNearEnd has been fired (fire only once)
      let nearEndFired = false;

      const startTime = performance.now();

      const animateShipFrame = () => {
        const elapsed = (performance.now() - startTime) / 1000; // Convert to seconds
        const progress = Math.min(elapsed / duration, 1.0);

        // Fire near-end callback once when travel reaches encounter reveal threshold
        if (
          !nearEndFired &&
          onNearEnd &&
          progress >= ANIMATION_CONFIG.ENCOUNTER_REVEAL_PROGRESS
        ) {
          nearEndFired = true;
          onNearEnd();
        }

        // Apply linear interpolation for constant velocity ship movement
        // No easing function - ship travels at constant speed
        currentPosition.copy(originPos).lerp(destPos, progress);
        this.shipIndicator.position.copy(currentPosition);
        this.shipReticle.position.copy(currentPosition);

        // Orient reticle to face camera for consistent appearance
        this.shipReticle.lookAt(this.camera.position);

        // Continue animation or resolve
        if (progress < 1.0) {
          requestAnimationFrame(animateShipFrame);
        } else {
          // Hide ship indicator and reticle when travel completes
          this.shipIndicator.visible = false;
          this.shipReticle.visible = false;
          resolve();
        }
      };

      // Start animation
      requestAnimationFrame(animateShipFrame);
    });
  }

  /**
   * Clean up animation state and restore controls
   *
   * Restores camera position, hides ship indicator, and unlocks controls.
   * Called on error or timeout to ensure the game remains in a valid state.
   */
  _cleanup() {
    // Restore camera to original position
    this._restoreCameraState();

    // Hide ship indicator if visible
    if (this.shipIndicator) {
      this.shipIndicator.visible = false;
    }

    // Hide reticle if visible
    if (this.shipReticle) {
      this.shipReticle.visible = false;
    }

    // Unlock controls
    this.inputLockManager.unlock();

    // Reset animation flag
    this.isAnimating = false;
  }

  /**
   * Play the complete jump animation sequence
   *
   * Orchestrates the full animation: lock input → zoom to side view → animate ship travel →
   * zoom back to original view → unlock input. Ensures controls are always restored even
   * if errors occur during animation.
   *
   * Includes timeout mechanism to force completion if animation hangs.
   * Handles edge cases like very close stars (< 1 LY) and very distant stars (> 15 LY).
   *
   * Game state (fuel, location, time) should already be updated before calling this method.
   * This ensures progress is saved even if the animation is interrupted.
   *
   * @param {number} originSystemId - Origin star system ID
   * @param {number} destinationSystemId - Destination star system ID
   * @param {Function|null} onTravelNearEnd - Optional callback invoked once when
   *   ship travel progress reaches the encounter reveal threshold
   * @returns {Promise<void>} Resolves when animation completes
   */
  async playJumpAnimation(
    originSystemId,
    destinationSystemId,
    onTravelNearEnd = null
  ) {
    // Prevent overlapping animations
    if (this.isAnimating) {
      console.warn('Animation already in progress, ignoring request');
      return;
    }

    this.isAnimating = true;

    // Set up timeout to force completion if animation hangs
    const timeoutId = setTimeout(() => {
      console.error(
        'Animation timeout: forcing completion after',
        ANIMATION_CONFIG.ANIMATION_TIMEOUT,
        'ms'
      );
      this._cleanup();
    }, ANIMATION_CONFIG.ANIMATION_TIMEOUT);

    try {
      // Find star systems in data
      const originSystem = this.starData.find((s) => s.id === originSystemId);
      const destinationSystem = this.starData.find(
        (s) => s.id === destinationSystemId
      );

      if (!originSystem || !destinationSystem) {
        throw new Error(
          `Invalid system IDs: origin=${originSystemId}, destination=${destinationSystemId}`
        );
      }

      // Get star positions using dedicated instance vectors
      // These persist throughout the animation sequence and are passed to async methods
      if (!this._jumpOriginPos) {
        this._jumpOriginPos = new THREE.Vector3();
      }
      if (!this._jumpDestPos) {
        this._jumpDestPos = new THREE.Vector3();
      }

      this._jumpOriginPos.set(originSystem.x, originSystem.y, originSystem.z);
      this._jumpDestPos.set(
        destinationSystem.x,
        destinationSystem.y,
        destinationSystem.z
      );

      // Calculate distance for timing
      const distance = this._jumpOriginPos.distanceTo(this._jumpDestPos);

      // Store original camera state before any transitions
      this._storeCameraState();

      // Lock input controls to prevent interaction during animation
      this.inputLockManager.lock();

      // Phase 1: Zoom in to side view
      try {
        const sideView = this.calculateSideViewPosition(
          this._jumpOriginPos,
          this._jumpDestPos,
          distance
        );
        const zoomDuration = AnimationTimingCalculator.calculateZoomDuration();
        await this.animateCameraTransition(
          sideView.position,
          sideView.lookAt,
          zoomDuration
        );
      } catch (error) {
        console.error('Error during zoom-in phase:', error);
        throw error;
      }

      // Phase 2: Animate ship travel from origin to destination
      try {
        await this.animateShipTravel(
          this._jumpOriginPos,
          this._jumpDestPos,
          distance,
          onTravelNearEnd
        );
      } catch (error) {
        console.error('Error during ship travel phase:', error);
        throw error;
      }

      // Phase 3: Zoom out back to original view
      try {
        if (this.originalCameraState) {
          const zoomDuration =
            AnimationTimingCalculator.calculateZoomDuration();
          await this.animateCameraTransition(
            this.originalCameraState.position,
            this.originalCameraState.target,
            zoomDuration
          );
        }
      } catch (error) {
        console.error('Error during zoom-out phase:', error);
        throw error;
      }

      // Restore camera state
      this._restoreCameraState();

      // Clear timeout since animation completed successfully
      clearTimeout(timeoutId);
    } catch (error) {
      // Log error for debugging
      console.error('Jump animation error:', error);

      // Clean up animation state
      this._cleanup();

      // Clear timeout
      clearTimeout(timeoutId);
    } finally {
      // Always unlock controls and reset state, even if animation fails
      this.inputLockManager.unlock();
      this.isAnimating = false;

      // Clear timeout in case it wasn't cleared above
      clearTimeout(timeoutId);
    }
  }

  /**
   * Dispose of animation system resources
   *
   * Properly disposes of sprite material and geometry to prevent GPU memory leaks.
   * Should be called when the animation system is no longer needed.
   */
  dispose() {
    if (this.shipIndicator) {
      // Dispose material and texture
      if (this.shipIndicator.material) {
        if (this.shipIndicator.material.map) {
          this.shipIndicator.material.map.dispose();
        }
        this.shipIndicator.material.dispose();
      }

      // Remove from scene
      this.scene.remove(this.shipIndicator);
      this.shipIndicator = null;
    }

    if (this.shipReticle) {
      // Dispose geometry and material
      if (this.shipReticle.geometry) {
        this.shipReticle.geometry.dispose();
      }
      if (this.shipReticle.material) {
        this.shipReticle.material.dispose();
      }

      // Remove from scene
      this.scene.remove(this.shipReticle);
      this.shipReticle = null;
    }
  }
}
